import * as React from 'react';
import { Box, type SxProps } from '@mui/material';
import { type NamedIconsType } from '@europrocurement/l2d-icons';
import { useAsyncStatus, useHandleError } from '@europrocurement/l2d-hooks';
import { useL2DTheme } from '@europrocurement/flexy-components';
import WidgetLayout from './WidgetLayout';
import { type WidgetLayoutProps } from './WidgetLayoutProps';

type WidgetWithActionProps = {
    title: string | React.ReactNode;
    icon: NamedIconsType;
    actionMessage: string;
    actionCallback: () => Promise<void>;
    data?: string | number;
    dataError?: boolean;
    onRetryData?: () => void;
    paragraph?: string | React.ReactNode;
    color?: string;
    loadingMessage?: string;
    beforeActionCallback?: () => void;
    onActionSuccessCallback?: () => void;
    onActionFailureCallback?: () => void;
    sx?: SxProps;
    sxData?: SxProps;
    fadeBgColor?: string;
};

const WidgetWithAction: React.FunctionComponent<WidgetWithActionProps> = function ({
    title,
    icon,
    actionMessage,
    actionCallback,
    data = undefined,
    dataError = false,
    onRetryData = undefined,
    paragraph = undefined,
    color = 'primary',
    loadingMessage = 'Chargement...',
    beforeActionCallback = () => {},
    onActionSuccessCallback = () => {},
    onActionFailureCallback = () => {},
    sx,
    fadeBgColor,
}: WidgetWithActionProps) {
    const [dataTimedOut, setDataTimedOut] = React.useState(false);
    const [retryCount, setRetryCount] = React.useState(0);
    const [hasSpace, setHasSpace] = React.useState(false);
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const cardRef = React.useRef<HTMLDivElement>(null);
    const { palette } = useL2DTheme();
    const { status, startLoading, setSuccess, setError } = useAsyncStatus();
    const { handleFailure } = useHandleError();

    // Detect available vertical space to decide if paragraph can coexist with counter
    React.useEffect(() => {
        if (!cardRef.current) return undefined;
        const observer = new ResizeObserver(([entry]) => {
            const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
            setHasSpace(height > 280);
        });
        observer.observe(cardRef.current);
        return () => observer.disconnect();
    }, []);

    React.useEffect(() => {
        if (data !== undefined || dataError) {
            if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
            return undefined;
        }
        timeoutRef.current = setTimeout(() => setDataTimedOut(true), 10_000);
        return () => {
            if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
        };
    }, [data, dataError, retryCount]);

    const handleRetryData = () => {
        setDataTimedOut(false);
        setRetryCount((c) => c + 1);
        if (onRetryData) onRetryData();
    };

    const handleActionClick = () => {
        beforeActionCallback();
        setTimeout(() => {
            startLoading();
            actionCallback()
                .then(() => {
                    onActionSuccessCallback();
                    setSuccess();
                })
                .catch((error: unknown) => {
                    onActionFailureCallback();
                    handleFailure({ error, setError });
                });
        }, 250);
    };

    const layoutProps: WidgetLayoutProps = {
        icon,
        title,
        data,
        dataError,
        dataTimedOut,
        onRetryData: handleRetryData,
        actionMessage,
        loadingMessage,
        isLoading: status.loading,
        onActionClick: handleActionClick,
        color,
        paragraph,
        hasSpace,
        fadeBgColor,
    };

    return (
        <Box
            ref={cardRef}
            sx={{
                width: '100%',
                height: '100%',
                backgroundColor: palette.background.paper,
                borderRadius: 2,
                boxShadow: `0px 2px 2.5px 0px ${palette.background.shadow}`,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                p: 2,
                ...sx,
            }}
        >
            <WidgetLayout {...layoutProps} />
        </Box>
    );
};

export default WidgetWithAction;
